// Employee sync functions for customer management
import api from '../../../shared/utils/api';
import { fetchEmployees } from './api';

const INSURANCE_AUTH_FAILED_MESSAGE =
  'НД нэвтрэх нэр, нууц үгээ шалгана уу. Портал дээр нэвтэрч чадахгүй бол нууц үг өөрчлөгдсөн эсвэл буруу хадгалагдсан байж магадгүй.';

function resolveInsuranceAuthMessage(error: any, fallbackMessage?: string): string {
  const responseCode = error?.response?.code || error?.responseData?.code;
  if (
    error?.code === 'INSURANCE_AUTH_FAILED' ||
    responseCode === 'INSURANCE_AUTH_FAILED'
  ) {
    return INSURANCE_AUTH_FAILED_MESSAGE;
  }
  return fallbackMessage || error?.message || 'Ажилчдын мэдээлэл синхрон хийхэд алдаа гарлаа';
}

// Handle fetch employees
export const handleFetchEmployees = async (
  customerId: string,
  insuranceLoginId: string,
  isNewCustomer: boolean = false,
  customerCode?: string,
  customerName?: string,
  customerOid?: string,
  setEmployees?: (employees: any[]) => void,
  setShowSuccessPopup?: (show: boolean) => void,
  setSuccessMessage?: (message: string) => void,
  setShowErrorPopup?: (show: boolean) => void,
  setErrorMessage?: (message: string) => void
) => {
  try {
    console.log('🔧 Fetching employees for customer:', {
      customerId,
      insuranceLoginId,
      isNewCustomer,
      customerCode,
      customerName,
      customerOid
    });
    
    if (!customerId || !insuranceLoginId) {
      throw new Error('Customer ID эсвэл Insurance Login ID байхгүй');
    }
    
    // Get insurance login credentials
    const currentAccountantOid = localStorage.getItem('accountantOid');
    if (!currentAccountantOid) {
      throw new Error('Accountant OID олдсонгүй');
    }
    
    const insuranceLoginsResponse = await api.get(`/api/insurance-login/${currentAccountantOid}`);
    
    if (!insuranceLoginsResponse.success || !insuranceLoginsResponse.data) {
      throw new Error('Insurance logins олдсонгүй');
    }
    
    const insuranceLogin = insuranceLoginsResponse.data.find((login: any) => login.Oid === insuranceLoginId);
    
    if (!insuranceLogin) {
      throw new Error('Insurance login олдсонгүй');
    }
    
    // IMPORTANT: insuranceLoginId parameter should be the OID (GUID), not the username
    // Backend expects the OID which it will use to look up username and password from InsuranceLogin table
    const insuranceLoginOid = insuranceLogin.Oid; // This is the GUID we need to send
    const insuranceUsername = insuranceLogin.InsuranceLoginId; // Username for reference only
    const insurancePassword = insuranceLogin.Password || insuranceLogin.InsuranceLoginPassword;
    
    if (!insuranceLoginOid) {
      throw new Error('Insurance login OID олдсонгүй');
    }
    
    console.log('🔧 Insurance credentials found:', {
      oid: insuranceLoginOid,
      username: insuranceUsername,
      password: insurancePassword ? '***' : 'NO PASSWORD'
    });
    
    // Step 1: Sync employees from insurance system
    // IMPORTANT: Send the OID (GUID), not the username
    // Backend will use this OID to get username and password from InsuranceLogin table
    console.log('📡 Step 1 - Syncing employees from insurance system...');
    console.log('📡 Step 1 - Using InsuranceLogin OID:', insuranceLoginOid);
    const syncResponse = await api.post(`/api/sync-employees/${customerId}`, {
      insuranceLoginId: insuranceLoginOid, // Send OID (GUID), not username!
      insuranceLoginPassword: insurancePassword, // Backend may not use this, but send it for compatibility
      customerCode: customerCode,
      customerName: customerName,
      isNewCustomer: isNewCustomer
    });
    
    if (!syncResponse.success) {
      console.error('❌ Failed to sync employees:', syncResponse.message);
      if (setShowErrorPopup && setErrorMessage) {
        setErrorMessage(resolveInsuranceAuthMessage(syncResponse, syncResponse.message));
        setShowErrorPopup(true);
      }
      return [];
    }
    
    console.log('✅ Employees synced successfully');
    
    // Step 2: Get customerOid from sync response or use provided customerOid
    let finalCustomerOid = customerOid;
    
    if (!finalCustomerOid && syncResponse.customerOid) {
      finalCustomerOid = syncResponse.customerOid;
      console.log('📡 Step 2 - Using customerOid from sync response:', finalCustomerOid);
    }
    
    if (!finalCustomerOid) {
      console.warn('⚠️ CustomerOid not found, cannot fetch employees from database');
      if (setShowSuccessPopup && setSuccessMessage) {
        setShowSuccessPopup(true);
        setSuccessMessage('Ажилчдын мэдээлэл амжилттай синхрон хийгдлээ (Ажилчдын жагсаалт татахад customerOid шаардлагатай)');
      }
      return syncResponse.employees || [];
    }
    
    // Step 3: Fetch employees from database using customerOid
    console.log('📡 Step 3 - Fetching employees from database...');
    try {
      const employees = await fetchEmployees(finalCustomerOid);
      console.log('✅ Employees fetched from database:', employees.length);
      
      // Update employees state if callback provided
      if (setEmployees) {
        setEmployees(employees);
      }
      
      if (setShowSuccessPopup && setSuccessMessage) {
        setShowSuccessPopup(true);
        setSuccessMessage(`Ажилчдын мэдээлэл амжилттай синхрон хийгдлээ (${employees.length} ажилчин)`);
      }
      
      return employees;
    } catch (fetchError: any) {
      console.error('❌ Failed to fetch employees from database:', fetchError);
      // Return synced employees if database fetch fails
      if (syncResponse.employees && syncResponse.employees.length > 0) {
        console.log('⚠️ Returning synced employees from sync response');
        if (setEmployees) {
          setEmployees(syncResponse.employees);
        }
        return syncResponse.employees;
      }
      throw fetchError;
    }
    
  } catch (error: any) {
    console.error('❌ Error syncing employees:', error);
    if (setShowErrorPopup && setErrorMessage) {
      setErrorMessage(resolveInsuranceAuthMessage(error));
      setShowErrorPopup(true);
    }
    return [];
  }
};

